import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../../config/theme";
import Modal from "../../../../components/base/Modal";
import { buildThemeClasses } from "../../theme";
import {
  fetchBlocks,
  createBlock,
  deleteBlock,
  getAvailability,
  fmtDateLong,
  fmtTime,
  type TimeBlock,
  type Availability,
} from "../../services/bookingsService";

import { DIALOG_COLORS } from "../../../../components/ui";
// ===========================================================================
// Bloqueos — LIVE list of time blocks. Blocked time removes those slots from
// the public scheduler so no call can be booked during focus time / PTO.
// ===========================================================================

export default function BloqueosLive() {
  const tc = buildThemeClasses();

  const [blocks, setBlocks] = useState<TimeBlock[]>([]);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const tz = availability?.timezone || "America/La_Paz";
  const offset = availability?.utcOffset || "-04:00";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bl, av] = await Promise.all([fetchBlocks(), getAvailability()]);
      setBlocks(bl);
      if (av) setAvailability(av);
    } catch (e) {
      const err = e as { message?: string };
      setError(err.message || "No se pudieron cargar los bloqueos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const now = Date.now();
  const { upcoming, past } = useMemo(() => {
    const up: TimeBlock[] = [];
    const pa: TimeBlock[] = [];
    for (const b of blocks) {
      (new Date(b.end).getTime() >= now ? up : pa).push(b);
    }
    up.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    pa.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
    return { upcoming: up, past: pa };
  }, [blocks, now]);

  const remove = async (block: TimeBlock) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar bloqueo",
      text: "El horario volverá a estar disponible para reservas.",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    setBlocks((prev) => prev.filter((b) => b.id !== block.id));
    try {
      await deleteBlock(block.id);
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar", confirmButtonColor: brand.primary });
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-500">
              <i className="ri-lock-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Bloqueos de tiempo</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Reserva tiempo para ti: esos horarios no aparecerán como disponibles
              </p>
            </div>
          </div>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400"
          >
            <i className="ri-add-line" /> Nuevo bloqueo
          </button>
        </div>

        {loading ? (
          <div className={`rounded-2xl border p-10 text-center text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
            <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando...
          </div>
        ) : error ? (
          <div className={`rounded-2xl border p-10 text-center ${tc.cardBorder} ${tc.cardBg}`}>
            <p className="text-sm text-red-500">{error}</p>
          </div>
        ) : blocks.length === 0 ? (
          <div className={`rounded-2xl border p-10 text-center text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
            <i className="ri-calendar-line mb-2 block text-3xl" />
            No hay bloqueos. Crea uno para evitar reuniones durante ese horario.
          </div>
        ) : (
          <div className="space-y-5">
            <BlockList title="Próximos" blocks={upcoming} tz={tz} tc={tc} onRemove={remove} />
            {past.length > 0 && <BlockList title="Pasados" blocks={past} tz={tz} tc={tc} onRemove={remove} dim />}
          </div>
        )}
      </div>

      {creating && (
        <BlockComposer
          tc={tc}
          offset={offset}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function BlockList({
  title,
  blocks,
  tz,
  tc,
  onRemove,
  dim,
}: {
  title: string;
  blocks: TimeBlock[];
  tz: string;
  tc: ReturnType<typeof buildThemeClasses>;
  onRemove: (b: TimeBlock) => void;
  dim?: boolean;
}) {
  if (blocks.length === 0) return null;
  return (
    <div>
      <p className={`mb-2 text-xs font-semibold uppercase tracking-wide ${tc.textSecondary}`}>{title}</p>
      <div className={`divide-y overflow-hidden rounded-2xl border ${tc.cardBorder} ${tc.cardBg} ${dim ? "opacity-60" : ""}`}>
        {blocks.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-3 p-3">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-500">
                <i className="ri-lock-line" />
              </span>
              <div>
                <p className={`text-sm font-medium ${tc.text}`}>{b.title || "Bloqueo"}</p>
                <p className={`text-xs ${tc.textSecondary}`}>
                  {fmtDateLong(b.start, tz)}
                  {b.allDay ? " · Todo el día" : ` · ${fmtTime(b.start, tz)}–${fmtTime(b.end, tz)}`}
                  {b.reason ? ` · ${b.reason}` : ""}
                </p>
              </div>
            </div>
            <button onClick={() => onRemove(b)} title="Eliminar" className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10">
              <i className="ri-delete-bin-line" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockComposer({
  tc,
  offset,
  onClose,
  onCreated,
}: {
  tc: ReturnType<typeof buildThemeClasses>;
  offset: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState("");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(today);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("10:00");
  const [allDay, setAllDay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!allDay && end <= start) {
      setErr("La hora de fin debe ser posterior al inicio.");
      return;
    }
    setBusy(true);
    try {
      await createBlock({
        title: title || "Bloqueo",
        reason,
        allDay,
        start: allDay ? `${date}T00:00:00${offset}` : `${date}T${start}:00${offset}`,
        end: allDay ? `${date}T23:59:00${offset}` : `${date}T${end}:00${offset}`,
      });
      onCreated();
    } catch (e) {
      const error = e as { message?: string };
      setErr(error.message || "No se pudo crear el bloqueo");
      setBusy(false);
    }
  };

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`;

  return (
    <Modal
      title="Nuevo bloqueo"
      themeClasses={tc}
      onClose={onClose}
      width="max-w-md"
      content={
        <div className="space-y-3">
          {err && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">{err}</p>}
          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Título</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tiempo personal" className={inputCls} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3 sm:col-span-1">
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Fecha</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
            {!allDay && (
              <>
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Desde</label>
                  <input type="time" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Hasta</label>
                  <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} />
                </div>
              </>
            )}
          </div>
          <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> Todo el día
          </label>
          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Motivo (opcional)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2 border-t pt-3">
            <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text}`}>
              Cancelar
            </button>
            <button onClick={submit} disabled={busy} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-60">
              {busy ? "Guardando..." : "Crear bloqueo"}
            </button>
          </div>
        </div>
      }
    />
  );
}
